import { formatPhp } from '@palihog/shared';
import { useId, useState } from 'react';
import type { QuoteRow } from '../../lib/types';
import { quoteCanBeApproved, quoteOriginLabel, type QuoteSnapshot } from './quoteIntegrity';

export interface QuoteDecisionInput {
  approve: boolean;
  quoteId: string;
  quoteVersion: number;
  acknowledgedQuoteVersion: number | null;
}

export function QuotationApprovalCard({ quote, snapshot, currentQuoteId, purchaseRequired, canDecide, pending, error, onDecision }: {
  quote: QuoteRow;
  snapshot: QuoteSnapshot;
  currentQuoteId: string | null;
  purchaseRequired: boolean;
  canDecide: boolean;
  pending: boolean;
  error: string | null;
  onDecision: (decision: QuoteDecisionInput) => void;
}) {
  const [acknowledgedQuote, setAcknowledgedQuote] = useState<string | null>(null);
  const acknowledgementId = useId();
  const approvedFeeCentavos = snapshot.totalDeliveryFeeCentavos + snapshot.serviceFeeCentavos;
  const quoteKey = `${quote.id}:${snapshot.version}`;
  const acknowledged = acknowledgedQuote === quoteKey;

  const decide = (approve: boolean) => onDecision({
    approve,
    quoteId: snapshot.quoteId,
    quoteVersion: snapshot.version,
    acknowledgedQuoteVersion: approve && purchaseRequired ? snapshot.version : null,
  });
  const approvalEnabled = quoteCanBeApproved(currentQuoteId, snapshot) && (!purchaseRequired || acknowledged);

  return <section className="card quote-card quotation-summary" aria-labelledby="quotation-summary-title">
    <div className="section-heading">
      <div><p className="eyebrow">{quoteOriginLabel(quote.distance_source)} · v{snapshot.version}</p><h2 id="quotation-summary-title">Quotation summary</h2></div>
      <span className="status-pill">For approval</span>
    </div>

    {quote.quote_items.length > 0 && <div className="note"><strong>Proposed item changes</strong>{quote.quote_items.map((item) => <p key={item.id}>{item.quantity}× {item.description} — {formatPhp(item.unit_price_centavos)} ({item.kind.replace('_', ' ')})</p>)}</div>}

    {purchaseRequired ? <>
      <div className="money-row total" data-testid="approved-service-fee"><span>Approved delivery/service fee</span><strong>{formatPhp(approvedFeeCentavos)}</strong></div>
      <div className="money-row muted" data-testid="purchase-spending-limit"><span>Authorized purchase budget <small>(spending limit only)</small></span><strong>{formatPhp(snapshot.purchaseBudgetCentavos)}</strong></div>
      <div className="money-row" data-testid="actual-receipt-amount"><span>Actual purchase amount (OR)</span><strong>To be determined</strong></div>
      <div className="note payment-formula"><strong>Final Payment = Approved Delivery/Service Fee + Actual Amount on Official Receipt (OR)</strong><p>{formatPhp(approvedFeeCentavos)} + Actual Amount on Official Receipt (OR)</p></div>
      <p>You will only reimburse the exact amount paid by the Rider for your items, based on the official receipt. No markup or hidden charges.</p>
      <div className="alert warning quotation-warning"><strong>Important note</strong><span>Please double-check the official receipt (OR) and purchased items before paying and providing your delivery confirmation code.</span></div>
    </> : <>
      <div className="money-row"><span>Delivery fee</span><strong>{formatPhp(snapshot.totalDeliveryFeeCentavos)}</strong></div>
      <div className="money-row"><span>Service fee</span><strong>{formatPhp(snapshot.serviceFeeCentavos)}</strong></div>
      <div className="money-row total"><span>Total quoted amount due</span><strong>{formatPhp(snapshot.totalQuotedDueCentavos)}</strong></div>
    </>}

    {quote.notes && <p className="muted">{quote.notes}</p>}
    <p className="fine-print">Only this exact quotation version can be approved. Customer approval permits dispatch only when dispatch is enabled and every server eligibility check passes.</p>

    {canDecide ? <>
      {purchaseRequired && <label className="checkbox-row quote-acknowledgement" htmlFor={acknowledgementId}><input id={acknowledgementId} type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledgedQuote(event.target.checked ? quoteKey : null)} />I understand that my final payment includes the {formatPhp(approvedFeeCentavos)} delivery/service fee plus the actual purchase amount shown on the official receipt.</label>}
      <div className="button-row quotation-actions"><button type="button" className="button subtle" disabled={pending} onClick={() => decide(false)}>Reject quotation</button><button type="button" className="button primary" disabled={pending || !approvalEnabled} onClick={() => decide(true)}>Approve quotation</button></div>
    </> : <p className="muted">Only the authorized customer can approve or reject this quotation. Manual revision remains an audited exception in the dispatcher desk.</p>}
    {error && <p className="alert error" role="alert">{error}</p>}
  </section>;
}
